import React, { useMemo, useState } from 'react';

import { CaseReport, CaseReportSegment } from '../../models/CaseTypes';
import AchievedCorrection from '../../components/Alignment/AchievedCorrection';
import { PerformanceFilterType } from './TabNavigation';
import { CaseStepBreakdownChart, StepData } from '../../components/Charts/CaseStepBreakdownChart';
import { CaseTimelineChart, TimelineSegmentData } from '../../components/Charts/CaseTimelineChart';
import { CannulationScrewPlacementChart } from '../../components/Charts/CannulationScrewPlacementChart';
import StepBreakdownAdapter from '../../models/StepBreakdownAdapter';
import TimelineAdapter from '../../models/TimelineAdapter';
import CannulationScrewPlacementAdapter, { CannulationBarSegment } from '../../models/CannulationScrewPlacementAdapter';
import { getSegmentKeyByLabel } from '../../utils/caseUtils';
import { Modal } from '../../components/common/Modal';
import { VideoPlayer, VideoPlayerParams } from '../../components/common/VideoPlayer';
import { NoVideoAvailable } from '../../components/common/NoVideoAvailable';
import { logTrace } from '../../utils/trackingUtils';

interface PerformanceSectionProps {
  caseReport: CaseReport;
  performanceFilter: PerformanceFilterType;
}

export function PerformanceSection({
  caseReport,
  performanceFilter: _performanceFilter
}: PerformanceSectionProps): React.JSX.Element {
  // Create adapters and get chart data
  const stepBreakdownAdapter = useMemo(() => new StepBreakdownAdapter(caseReport), [caseReport]);
  const stepData = useMemo(() => stepBreakdownAdapter.getStepBreakdownData(), [stepBreakdownAdapter]);
  const totalStepTime = useMemo(() => stepBreakdownAdapter.calculateTotalStepTime(), [stepBreakdownAdapter]);

  const timelineAdapter = useMemo(() => new TimelineAdapter(caseReport), [caseReport]);
  const timelineData = useMemo(() => timelineAdapter.getTimelineData(), [timelineAdapter]);
  const totalNavigatedTime = useMemo(() => timelineAdapter.formatTotalNavigatedTime(), [timelineAdapter]);

  const cannulationAdapter = useMemo(() => new CannulationScrewPlacementAdapter(caseReport), [caseReport]);
  const cannulationData = useMemo(() => cannulationAdapter.getCannulationData(), [cannulationAdapter]);
  const avgTimePerLevel = useMemo(() => cannulationAdapter.getAverageTimePerLevel(), [cannulationAdapter]);
  const cannulationActivityTypes = useMemo(() => cannulationAdapter.getActivityTypes(), [cannulationAdapter]);

  // Video player state
  const [videoSegment, setVideoSegment] = useState<VideoPlayerParams | null>(null);
  const [showUnavailableModal, setShowUnavailableModal] = useState<boolean>(false);
  const [unavailableStepName, setUnavailableStepName] = useState<string>('');

  // Get segments by type
  const getSegmentsByType = (segments: CaseReportSegment[], type: string): CaseReportSegment[] => {
    return segments?.filter(segment => segment.segmentType === type) || [];
  };

  // Handle step click - opens video for that segment type
  const handleStepClick = (step: StepData): void => {
    if (!caseReport.segments || caseReport.segments.length === 0) {
      return;
    }

    const segmentType = getSegmentKeyByLabel(step.name);
    const segments = getSegmentsByType(caseReport.segments, segmentType);

    // Order segments by start time (use spread to avoid mutating the original array)
    const orderedSegments = [...segments].sort((a, b) => a.normalizedStartMin - b.normalizedStartMin);
    if (orderedSegments.length === 0) {
      return;
    }

    const firstSegment = orderedSegments[0];
    const lastSegment = orderedSegments[orderedSegments.length - 1];

    // Check if video is available
    if (!firstSegment?.signedUrl || firstSegment.signedUrl.length === 0) {
      setUnavailableStepName(step.name);
      setShowUnavailableModal(true);
      return;
    }

    if (!firstSegment.videoIsValid || !lastSegment.videoIsValid) {
      setUnavailableStepName(step.name);
      setShowUnavailableModal(true);
      return;
    }

    // Log trace only after all validations pass
    logTrace('PerformanceSection', 'proprio_bar_click_' + step.name, step.name);

    setVideoSegment({
      videoUrl: firstSegment.signedUrl,
      startTimeSeconds: firstSegment.videoStartSec ?? 0,
      endTimeSeconds: lastSegment.videoEndSec ?? 0,
      segmentTitle: step.name
    });
  };

  // Handle video close
  const handleCloseVideo = (): void => {
    if (videoSegment) {
      logTrace('PerformanceSection', 'proprio_video_close_' + videoSegment.segmentTitle, videoSegment.segmentTitle);
    }
    setVideoSegment(null);
  };

  // Handle timeline segment click - opens video for that specific segment
  const handleTimelineClick = (segment: TimelineSegmentData): void => {
    // Check if video is available
    if (!segment.signedUrl || segment.signedUrl.length === 0) {
      setUnavailableStepName(segment.segmentType);
      setShowUnavailableModal(true);
      return;
    }

    if (!segment.videoIsValid) {
      setUnavailableStepName(segment.segmentType);
      setShowUnavailableModal(true);
      return;
    }

    logTrace('PerformanceSection', 'proprio_timeline_click_' + segment.segmentType, segment.segmentType);

    setVideoSegment({
      videoUrl: segment.signedUrl,
      startTimeSeconds: segment.videoStartSec ?? 0,
      endTimeSeconds: segment.videoEndSec ?? 0,
      segmentTitle: segment.segmentType
    });
  };

  // Handle cannulation chart segment click - opens video for that activity at that locus
  const handleCannulationClick = (locus: string, segment: CannulationBarSegment): void => {
    // Check if video info is available
    if (!segment.videoInfo) {
      setUnavailableStepName(`${segment.label} at ${locus}`);
      setShowUnavailableModal(true);
      return;
    }

    if (!segment.videoInfo.videoIsValid || !segment.videoInfo.signedUrl) {
      setUnavailableStepName(`${segment.label} at ${locus}`);
      setShowUnavailableModal(true);
      return;
    }

    logTrace('PerformanceSection', 'proprio_cannulation_click_' + segment.activityType, `${locus}_${segment.activityType}`);

    setVideoSegment({
      videoUrl: segment.videoInfo.signedUrl,
      startTimeSeconds: segment.videoInfo.videoStartSec,
      endTimeSeconds: segment.videoInfo.videoEndSec,
      segmentTitle: `${segment.label} - ${locus}`
    });
  };

  return (
    <div className="flex flex-col gap-4 lg:gap-5">
      {/* Case Step Breakdown + Timeline Row - Mobile: stacked, Desktop: side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5 items-stretch">
        <div className="flex flex-col h-full">
          <CaseStepBreakdownChart
            data={stepData}
            totalStepTime={totalStepTime}
            onSegmentClick={handleStepClick}
          />
        </div>

        {/* Case Timeline */}
        <div className="flex flex-col h-full">
          <CaseTimelineChart
            segments={timelineData}
            totalTime={totalNavigatedTime}
            onSegmentClick={handleTimelineClick}
          />
        </div>
      </div>

      {/* Cannulation & Screw Placement Efficiency Chart - Full width */}
      {cannulationData.length > 0 && (
        <CannulationScrewPlacementChart
          data={cannulationData}
          avgTimePerLevel={avgTimePerLevel}
          activityTypes={cannulationActivityTypes}
          onBarClick={handleCannulationClick}
        />
      )}

      {/* 3D Alignment Section - Responsive Layout */}
      <AchievedCorrection caseReport={caseReport} />

      {/* Video Player Modal */}
      {videoSegment && (
        <Modal
          title={videoSegment.segmentTitle}
          onClose={handleCloseVideo}
        >
          <VideoPlayer
            key="segment-video-player"
            videoUrl={videoSegment.videoUrl}
            startTimeSeconds={videoSegment.startTimeSeconds}
            endTimeSeconds={videoSegment.endTimeSeconds}
            segmentTitle={videoSegment.segmentTitle}
          />
        </Modal>
      )}

      {/* Video Unavailable Modal */}
      {showUnavailableModal && (
        <Modal
          title={unavailableStepName}
          onClose={() => setShowUnavailableModal(false)}
        >
          <NoVideoAvailable />
        </Modal>
      )}
    </div>
  );
}
